import {
  type AccordionGroup,
  MyNaviFilterAccordionList,
} from '@/components/Campaign/mynavi/MyNaviModels/Filter/MyNaviFilterAccordionList'
import { MyNaviFilterLayout } from '@/components/Campaign/mynavi/MyNaviModels/Filter/MyNaviFilterLayout'
import { useApi } from '@/core/api/useApi'
import type { MynaviStudyFieldNode } from '@/schemas/v2/mynavi'
import { Loader } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'

type Props = {
  initialStudyFieldIds: number[]
  onSelect: (studyFieldIds: number[]) => void
  onClear: () => void
  onClose: () => void
  studyFields?: MynaviStudyFieldNode[]
}

export function FilterStudyField({
  initialStudyFieldIds,
  onSelect,
  onClear,
  onClose,
  studyFields: studyFieldsProp,
}: Props) {
  const api = useApi()

  const [localSelected, setLocalSelected] = useState<number[]>(initialStudyFieldIds)

  const { data: studyFieldsData, isLoading } = useQuery({
    queryKey: ['mynavi-study-fields'],
    queryFn: () => api.guest.fetchMynaviStudyFields(false),
    staleTime: 5 * 60 * 1000,
    enabled: studyFieldsProp === undefined,
  })

  const { data: countData } = useQuery({
    queryKey: ['mynavi-filter-count', 'study-field', localSelected],
    queryFn: () => api.guest.fetchMynaviCollegesFilterCount({ studyFieldIds: localSelected }),
    enabled: studyFieldsProp === undefined,
    staleTime: 30 * 1000,
  })

  const resolvedStudyFields: MynaviStudyFieldNode[] = studyFieldsProp ?? studyFieldsData?.studyFields ?? []

  const handleToggleItem = useCallback((id: number) => {
    setLocalSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
  }, [])

  const handleToggleGroupAll = useCallback(
    (ids: number[]) => {
      const allSelected = ids.every((id) => localSelected.includes(id))
      if (allSelected) {
        setLocalSelected((prev) => prev.filter((id) => !ids.includes(id)))
      } else {
        setLocalSelected((prev) => Array.from(new Set([...prev, ...ids])))
      }
    },
    [localSelected],
  )

  const groups: AccordionGroup[] = useMemo(() => {
    return resolvedStudyFields.map((topField) => {
      const leafIds = (topField.children ?? []).map((child) => child.id)
      return {
        name: topField.name,
        defaultExpanded: false,
        allChecked: leafIds.length > 0 && leafIds.every((id) => localSelected.includes(id)),
        onToggleAll: () => handleToggleGroupAll(leafIds),
        items: (topField.children ?? []).map((child) => ({
          id: child.id,
          label: child.name,
          checked: localSelected.includes(child.id),
          onToggle: () => handleToggleItem(child.id),
        })),
      }
    })
  }, [resolvedStudyFields, localSelected, handleToggleGroupAll, handleToggleItem])

  const handleSelect = () => {
    onSelect(localSelected)
  }

  const handleClear = () => {
    setLocalSelected([])
    onClear()
  }

  return (
    <MyNaviFilterLayout
      title="学べる学問を選択する"
      onClose={onClose}
      searchResultCount={countData?.count ?? null}
      onSelect={handleSelect}
      onClear={handleClear}
    >
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader size="sm" />
        </div>
      ) : (
        <MyNaviFilterAccordionList groups={groups} />
      )}
    </MyNaviFilterLayout>
  )
}
